import { MonetizableEnum } from '@ria-client/segment/enums';
import {
  BankingSubProductTypeEnum,
  MarketplaceEntityTypeEnum,
} from '@ria-client/supergraph/marketplace/enums';
import type { ProductCard } from '@ria-client/supergraph/roundup/types';
import type { StructuredContentDocument } from '@ria-client/supergraph/structuredContent/types';
import { ORGANIZATION_ID } from '@ria-client/supergraph/utils/organization';
import type { ItemList, ListItem, WithContext } from 'schema-dts';

function formatTermLength(cdDepositTerm: number | null | undefined): string {
  if (!cdDepositTerm) return '';
  const years = Math.floor(cdDepositTerm / 12);
  const months = cdDepositTerm % 12;
  if (years > 0 && months > 0) return `${years}-year ${months}-month`;
  return years > 0 ? `${years}-year` : `${months}-month`;
}

function parseCurrencyToNumber(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Number.parseFloat(value.replace(/[$,]/g, ''));
  return Number.isNaN(parsed) ? undefined : parsed;
}

function extractStructuredContent(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) return content.map(extractStructuredContent).join(' ');
  if (!content || typeof content !== 'object' || !('props' in content)) return '';
  return extractStructuredContent((content as { props?: { children?: unknown } }).props?.children);
}

interface GetListItemSchema {
  entity: ProductCard['marketplaceEntity'];
  url: string;
  pros?: StructuredContentDocument[];
  cons?: StructuredContentDocument[];
}

function getItemListSchema({
  entity,
  url,
  category,
  description,
  minimumBalance,
  unitText,
  pros,
  cons,
}: GetListItemSchema & {
  category: string;
  description: string;
  minimumBalance: number;
  unitText: string;
}): WithContext<ListItem> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ListItem',
    item: {
      '@type': 'Product',
      name: entity.displayName,
      image: entity.image?.source,
      url: `${url}#${entity.id}`,
      identifier: entity.id,
      category,
      description,
      offers: [
        {
          '@type': 'Offer',
          availability:
            entity.monetizable === MonetizableEnum.yesAssumed
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: minimumBalance,
            priceCurrency: 'USD',
            unitText,
          },
        },
      ],
      ...(entity.starRating
        ? {
            review: {
              '@type': 'Review',
              author: { '@id': ORGANIZATION_ID },
              reviewRating: { '@type': 'Rating', ratingValue: entity.starRating, bestRating: 5 },
              ...(pros
                ? {
                    positiveNotes: {
                      '@type': 'ItemList',
                      itemListElement: pros.map((pro, index) => ({
                        '@type': 'ListItem',
                        position: index,
                        name: extractStructuredContent(pro),
                      })),
                    },
                  }
                : {}),
              ...(cons
                ? {
                    negativeNotes: {
                      '@type': 'ItemList',
                      itemListElement: cons.map((con, index) => ({
                        '@type': 'ListItem',
                        position: index,
                        name: extractStructuredContent(con),
                      })),
                    },
                  }
                : {}),
            },
          }
        : {}),
    },
  };
}

function getBankingProductSchema({ entity, url, pros, cons }: GetListItemSchema) {
  if (entity.entityType === MarketplaceEntityTypeEnum.Institutions) {
    const category = entity.institutionType
      ? entity.institutionType
          .split('_')
          .map(word => `${word[0]?.toUpperCase()}${word.slice(1).toLowerCase()}`)
          .join(' ')
      : 'Banking institution';
    return getItemListSchema({
      entity,
      url,
      category,
      pros,
      cons,
      description: `${entity.displayName} - ${category}`,
      minimumBalance: entity.offerForCta?.minimumBalance || 0,
      unitText: `${entity.offerForCta?.driverMinimumBalance?.value || 'No'} minimum deposit required to open`,
    });
  }

  const category =
    entity.subProductType === BankingSubProductTypeEnum.CD
      ? entity.product?.institution?.institutionType === 'credit_union'
        ? 'Share Certificates'
        : 'Certificates of Deposit'
      : entity.subProductTypeLabel || 'Banking account';
  const termLength =
    entity.subProductType === BankingSubProductTypeEnum.CD
      ? ` - ${formatTermLength(entity.cdDepositTerm)}`
      : '';
  return getItemListSchema({
    entity,
    url,
    category,
    pros,
    cons,
    description: `${entity.displayName} - ${category}${termLength}`,
    minimumBalance: entity.minimumBalance || 0,
    unitText: `${entity.driverMinimumBalance?.value || 'No'} minimum deposit required to open`,
  });
}

function getInvestingProductSchema({ entity, url, pros, cons }: GetListItemSchema) {
  const category = entity.subProductType || 'Investing account';
  return getItemListSchema({
    entity,
    url,
    category,
    pros,
    cons,
    description: `${entity.displayName} - ${category}`,
    minimumBalance: parseCurrencyToNumber(entity.driverAccountMinimum?.value) ?? 0,
    unitText: `${entity.driverAccountMinimum?.value || 'No'} minimum account balance required`,
  });
}

export function getProductListSchema({
  offers,
  title,
  url,
}: {
  offers: ProductCard[];
  title: string;
  url: string;
}): WithContext<ItemList> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${url}#product-list`,
    name: title,
    numberOfItems: offers.length,
    itemListElement: offers.map((offer, index) => ({
      ...(offer.marketplaceEntity.productType === 'banking'
        ? getBankingProductSchema({
            entity: offer.marketplaceEntity,
            url,
            pros: offer.pros,
            cons: offer.cons,
          })
        : getInvestingProductSchema({
            entity: offer.marketplaceEntity,
            url,
            pros: offer.pros,
            cons: offer.cons,
          })),
      position: index + 1,
    })),
  };
}
